"use client";

import { useEffect, useState } from "react";

import { LoadingState } from "@/components/shared/LoadingState";
import { usePathname, useRouter } from "@/i18n/navigation";
import { hasValidBetaAccess, requiresBetaAccess } from "@/lib/access/beta";

interface BetaAccessGuardProps {
  children: React.ReactNode;
}

/**
 * BetaAccessGuard - Protects non-production environments with beta access code
 *
 * - Exempts localhost/local development
 * - Checks NEXT_PUBLIC_APP_ENV environment variable
 * - Redirects unauthorized users to /beta-access page
 * - Validates stored beta access codes
 */
export function BetaAccessGuard({ children }: BetaAccessGuardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const needsBetaProtection = requiresBetaAccess();
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  // Prevent hydration mismatch by not rendering until mounted
  // This avoids issues where server and client render differently, runtime hydration warnings
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted || !needsBetaProtection) return;

    async function checkAccess() {
      // Don't protect the beta-access page itself to avoid redirect loop
      if (pathname === "/beta-access") {
        setIsChecking(false);
        setHasAccess(true);
        return;
      }

      // Check if user has valid beta access
      const accessGranted = await hasValidBetaAccess();
      setHasAccess(accessGranted);
      setIsChecking(false);

      console.log("BetaAccess: ", accessGranted);

      // Redirect to beta-access page if access is not granted
      if (!accessGranted) {
        router.replace("/beta-access");
      }
    }

    checkAccess();
  }, [pathname, router, isMounted, needsBetaProtection]);

  // Guarded render order:
  // 1. Don't attempt access checks until mounted (avoid hydration mismatch)
  // 2. If protection is required and we're still checking, show loading
  // 3. If protection is required and access is denied, render nothing (redirect handled in effect)
  // 4. Otherwise render children
  if (isMounted && needsBetaProtection) {
    if (isChecking) {
      return (
        <div className="fixed inset-0 bg-background">
          <LoadingState />
        </div>
      );
    }

    if (!hasAccess) return null;
  }

  return <>{children}</>;
}
